import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCalendar, 
  faTag, 
  faDollarSign, 
  faPercent, 
  faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import { useMccCodes } from '../contexts/MccContext';
import MccEditModal from './MccEditModal';
import { getCurrencyName } from '../utils/currencyCodes';

const TransactionModal = ({ transaction, onClose }) => {
  const { t } = useTranslation();
  const { getMccDescription } = useMccCodes();
  const [editMccData, setEditMccData] = useState(null);

  if (!transaction) return null;

  // Helper to format currency
  const formatMoney = (amount) => {
    return (amount / 100).toFixed(2);
  };

  const date = new Date(transaction.time);
  const formattedDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

  const isExpense = transaction.amount < 0;
  const colorClass = isExpense ? 'text-red-500' : 'text-green-500';

  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#1c1c1e] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 text-white">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#252528]">
          <h2 className="text-lg font-semibold text-white">{t('transactionDetails', 'Деталі транзакції')}</h2>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-700 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>

        {/* Amount Hero */}
        <div className="flex flex-col items-center justify-center p-8 bg-[#1c1c1e]">
           <div className={`text-4xl font-bold ${colorClass} mb-2`}>
             {formatMoney(transaction.amountEur || transaction.amount)}
             <span className="text-xl ml-1">EUR</span> 
           </div>
           
           {(transaction.amountEur !== transaction.amount) && (
             <div className="text-gray-400 text-sm">
               {formatMoney(transaction.amount)} {getCurrencyName(transaction.currencyCode)}
             </div>
           )}

           <p className="text-white font-medium mt-2 text-center">{transaction.description}</p>
           <p className="text-gray-500 text-sm mt-1">{transaction.accountName}</p>
        </div>

        {/* Details Grid */}
        <div className="p-4 space-y-4">
          
          <div className="flex items-center justify-between p-3 bg-[#252528] rounded-xl">
             <div className="flex items-center gap-3">
               <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400 w-8 h-8 flex items-center justify-center">
                 <FontAwesomeIcon icon={faCalendar} />
               </div>
               <span className="text-gray-300 text-sm">{t('date', 'Дата')}</span>
             </div>
             <span className="text-white font-medium text-sm">{formattedDate}</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-[#252528] rounded-xl">
             <div className="flex items-center gap-3">
               <div className="p-2 bg-purple-500/10 rounded-lg text-purple-400 w-8 h-8 flex items-center justify-center">
                 <FontAwesomeIcon icon={faTag} />
               </div>
               <span className="text-gray-300 text-sm">{t('category', 'Категорія')}</span>
             </div>
              <span className="text-white font-medium text-sm text-right">
                {(() => {
                  const code = String(transaction.originalMcc || transaction.mcc);
                  const description = getMccDescription(code);
                  const isUnknown = description === getMccDescription('000000');
                  
                  if (isUnknown) {
                      return (
                          <button 
                            onClick={() => setEditMccData({ code })}
                            className="text-primary-400 hover:text-primary-300 hover:underline text-right"
                          >
                            {description} ({code})
                          </button>
                      );
                  }
                  
                  return description;
                })()}
             </span>
          </div>

          {(transaction.cashbackAmount > 0) && (
             <div className="flex items-center justify-between p-3 bg-[#252528] rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/10 rounded-lg text-yellow-400 w-8 h-8 flex items-center justify-center">
                    <FontAwesomeIcon icon={faArrowDown} />
                  </div>
                  <span className="text-gray-300 text-sm">{t('cashback', 'Кешбек')}</span>
                </div>
                <span className="text-white font-medium text-sm">
                  {formatMoney(transaction.cashbackAmount)}
                </span>
             </div>
          )}

          {(transaction.commissionRate > 0) && (
             <div className="flex items-center justify-between p-3 bg-[#252528] rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg text-red-400 w-8 h-8 flex items-center justify-center">
                    <FontAwesomeIcon icon={faPercent} />
                  </div>
                  <span className="text-gray-300 text-sm">{t('commission', 'Комісія')}</span>
                </div>
                <span className="text-white font-medium text-sm">
                  {formatMoney(transaction.commissionRate)}
                </span>
             </div>
          )}

        </div>
      </div>

       {editMccData && (
        <MccEditModal 
            isOpen={!!editMccData}
            onClose={() => setEditMccData(null)}
            initialData={editMccData}
            zIndex={10000}
            backdropClass="bg-black/20"
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TransactionModal;
